import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { push, replace } from 'react-router-redux'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import WizardNavButtons from '../WizardNavButtons'
import WizardStepper, { buildConfigurePath } from '../WizardStepper'

const t = buildTranslate('recovery_ai.wizard.processing')

// Cosmetic calibration delay before advancing into the wizard (the Testing /
// Trial-Sandbox step). No backend calibration-status signal exists yet.
//
// AI-321 (decision LOCKED 2026-07-08): KEEP this cosmetic auto-advance — do NOT
// switch to a manual Continue. The delay is intentional perceived-work UX (it
// makes the merchant feel substantial configuration is happening), and Continue
// stays disabled during it BY DESIGN (see the continueDisabled nav button below).
// TODO: replace this fixed delay with a real /recovery/configuration
// calibration-status poll once that endpoint lands. That swap is a FUTURE unit,
// out of scope here — this decision only locks the auto-advance behavior.
export const PROCESSING_REDIRECT_MS = 4000

// AI-323 (a11y): announce "configuration complete" this many ms BEFORE the
// screen advances, so a screen reader hears completion rather than silence when
// the route changes. Doubles as the all-done beat: the checklist finishes
// ticking at this lead point, so the announcement and the last green tick land
// together by construction rather than on two timers that could drift.
export const CALIBRATION_COMPLETE_LEAD_MS = 600

// Window the checklist animates in: every item is ticked green by the time the
// completion announcement fires, leaving a short all-done beat before the
// screen advances. Derived from the two constants above so the total stays at
// PROCESSING_REDIRECT_MS (AI-321 timing untouched).
export const CHECKLIST_BUDGET_MS =
  PROCESSING_REDIRECT_MS - CALIBRATION_COMPLETE_LEAD_MS

// Per-item timing jitter so the checklist reads as work finishing at its own
// pace rather than a metronome. ±25% is the band each raw WEIGHT is drawn in,
// not the spread of the resulting slice — normalizing to a fixed budget measures
// each draw against the other four, so with 5 items the actual slices span
// ~443-1000ms against a 680ms even slice. Raising this widens that faster than
// it looks; the absolute floor is enforced by a test, not by this constant.
export const CHECKLIST_JITTER = 0.25

// Cumulative ms offsets at which each checklist item flips to done. Weights are
// drawn within the jitter band then normalized to the budget, so the jitter
// redistributes the slices without ever moving the finish line — the last
// offset is pinned to budgetMs so float drift can't desync the final tick from
// the completion announcement.
export const buildChecklistSchedule = (
  budgetMs: number,
  count: number,
  random: () => number = Math.random
): number[] => {
  const weights = Array.from(
    { length: count },
    () => 1 - CHECKLIST_JITTER + random() * CHECKLIST_JITTER * 2
  )
  const scale = budgetMs / weights.reduce((sum, weight) => sum + weight, 0)
  const offsets: number[] = []
  let elapsed = 0
  weights.forEach(weight => {
    elapsed += weight * scale
    offsets.push(elapsed)
  })
  offsets[count - 1] = budgetMs
  return offsets
}

// AI-323 (WCAG 4.1.3): id of the note explaining WHY Continue is disabled during
// calibration, wired to the disabled Continue via aria-describedby so the
// blocking reason is programmatically determinable. Mirrors the MECHANISM of the
// Finalize ACTIVATION_INCOMPLETE_CUE_ID cue in Wizard/index.tsx (id'd note ←
// aria-describedby, ENG-984); the presentation differs deliberately — this
// note is sr-only, not a visible role="alert", to avoid double-announcing with
// the calibration status region above.
export const CONTINUE_CUE_ID = 'rai-calibration-in-progress'

// The calibration screen shows Train + Testing reached (matches the R12 mock);
// Locations + Escalations remain upcoming. The stepper checks the current stage
// and all earlier ones, so 'testing' yields exactly Train + Testing checked.
const STEPPER_POSITION = 'testing'

type ChecklistStatus = 'done' | 'active' | 'pending'

export const CHECKLIST_KEYS = [
  'trust',
  'generosity',
  'brand_voice',
  'rewards',
  'escalation',
]

// Each item carries two labels: the progressive form reads while it is spinning
// (and for the items still queued behind it), the past-tense form once ticked.
const checklistLabelKey = (key: string, status: ChecklistStatus) =>
  `steps.${key}.${status === 'done' ? 'done' : 'active'}`

// Status is derived from how many items have ticked over, so exactly one item
// is ever spinning and the list can only move forwards.
const statusForIndex = (
  index: number,
  completedCount: number
): ChecklistStatus => {
  if (index < completedCount) return 'done'
  if (index === completedCount) return 'active'
  return 'pending'
}

const ICON_BY_STATUS: Record<ChecklistStatus, string> = {
  done: 'fa-check-circle',
  active: 'fa-circle-o-notch fa-spin',
  pending: 'fa-circle-o',
}

const PENDING_OPACITY = 0.5

const ICON_FONT_SIZE = '18px'

// AI-323 (WCAG 2.3.3): the active checklist item renders a spinning icon
// (fa-spin) that keeps animating even for a prefers-reduced-motion user
// (aria-hidden silences it for a screen reader, not visually). Freeze it to a
// static icon for those users. Extracted + exported so the gating logic is
// unit-testable directly — styletron's css() is a no-op in the jsdom test env,
// so the emitted media rule can't be asserted through the rendered DOM.
export const REDUCED_MOTION_FREEZE: StyleObject = {
  // '!important' is required: FA4's `.fa-spin { animation: fa-spin ... }` and this
  // styletron atomic rule are both single-class (equal specificity), so the winner is
  // decided by <head> source order — and styletron's injected <style> is NOT guaranteed
  // to fall after the compiled index.scss/FA blob. FA4's rule has no '!important', so
  // '!important' here beats it regardless of order. Verified in-browser 2026-07-08.
  '@media (prefers-reduced-motion: reduce)': { animation: 'none !important' },
}

// Style for one checklist icon. Only the 'active' status carries fa-spin, so
// only it gets the reduced-motion freeze.
export const buildChecklistIconStyle = (
  status: ChecklistStatus
): StyleObject => ({
  fontSize: ICON_FONT_SIZE,
  color:
    status === 'done'
      ? merchantTheme.colors.spearmint50
      : merchantTheme.colors.grey70,
  ...(status === 'active' ? REDUCED_MOTION_FREEZE : {}),
})

// Calibration interstitial (R12). Shown after Train, before Testing: framed by
// the persistent teal stepper chrome (Train + Testing reached) with Continue
// disabled; advances to the Testing step after a fixed calibration delay.
const Processing = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // Drawn once per mount (lazy initializer) so re-renders can't re-roll the
  // jitter mid-progression.
  const [schedule] = useState(() =>
    buildChecklistSchedule(CHECKLIST_BUDGET_MS, CHECKLIST_KEYS.length)
  )
  const [completedCount, setCompletedCount] = useState(0)
  // AI-323 (a11y): also drives the sr-only status announcer — the last tick IS
  // completion, so there is no separate phase to keep in sync.
  const isComplete = completedCount === CHECKLIST_KEYS.length
  // AI-323 (a11y): focus target for the interstitial heading on mount (see below).
  const headingRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // One timer per checklist item plus the redirect, all within the same fixed
    // budget (total = PROCESSING_REDIRECT_MS, so AI-321 timing is unchanged).
    // Scheduled up-front from cumulative offsets rather than chained, so a slow
    // frame can't compound into drift. All cleared on unmount.
    const checklistTimers = schedule.map((offset, index) =>
      setTimeout(() => setCompletedCount(index + 1), offset)
    )
    const redirectTimer = setTimeout(() => {
      // Advance via replace (not push) so the interstitial is NOT left on the
      // history stack. Otherwise browser Back from Testing lands here, this timer
      // re-fires, and the merchant is bounced forward again — a Back-button trap
      // (the route stays reachable as long as Train is complete). With replace,
      // Back from Testing goes to Train, as expected.
      dispatch(replace(buildConfigurePath('testing')))
    }, PROCESSING_REDIRECT_MS)
    return () => {
      checklistTimers.forEach(clearTimeout)
      clearTimeout(redirectTimer)
    }
  }, [dispatch, schedule])

  // AI-323 (a11y): move focus onto the interstitial heading region on mount. When
  // Train's Continue is clicked it unmounts and focus would otherwise fall to
  // <body>, orphaning keyboard/SR users on this auto-advancing screen. This is
  // the standard SPA route-change focus fix.
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <div
      className={css({
        backgroundColor: merchantTheme.colors.grey05,
        minHeight: '100vh',
      })}
    >
      {/* AI-323 (a11y): sr-only polite status region announcing calibration
          progress → completion for the auto-advancing screen. Kept separate from
          the visible headline (which focus-on-mount lands the SR on) so it is not
          re-announced on unrelated re-renders and does not double-read the
          heading. role="status" is implicitly polite; aria-live is explicit for
          clarity. */}
      <span role="status" aria-live="polite" className="sr-only">
        {isComplete ? t('status_complete') : t('status_calibrating')}
      </span>

      {/* Persistent teal stepper chrome — Continue is disabled during calibration. */}
      <div
        className={css({
          backgroundColor: merchantTheme.colors.spearmint50,
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px 0 32px',
        })}
      >
        <WizardStepper currentStep={STEPPER_POSITION} />
        {/* AI-323 (WCAG 4.1.3): sr-only note naming why Continue is disabled,
            referenced by the button's aria-describedby below. Kept sr-only (the
            screen auto-unblocks in seconds, so a visible caption would be noise)
            and NOT a second role="status"/live region — that would double-announce
            with the calibration status announcer above. */}
        <span id={CONTINUE_CUE_ID} className="sr-only">
          {t('continue_disabled_reason')}
        </span>
        <WizardNavButtons
          onCancel={() => dispatch(push('/recovery_ai'))}
          onContinue={() => undefined}
          continueDisabled
          continueAriaDescribedby={CONTINUE_CUE_ID}
        />
      </div>

      <div
        className={`d-flex flex-column align-items-center text-center ${css({
          maxWidth: '720px',
          margin: '0 auto',
          padding: '64px 24px',
          gap: '16px',
        })}`}
      >
        <i
          role="img"
          aria-label={t('logo_alt')}
          className={`fa fa-magic ${css({
            fontSize: '40px',
            color: merchantTheme.colors.spearmint50,
          })}`}
        />
        {/* AI-323 (a11y): focusable heading region (tabIndex={-1}) that receives
            focus on mount so keyboard/SR focus isn't orphaned on <body> when the
            user advances in from Train. outline:none suppresses a stray ring on
            programmatic focus of this non-interactive container. Wraps only the
            headline so the sibling flex gap to the subtitle is preserved. */}
        <div
          ref={headingRef}
          tabIndex={-1}
          data-testid="processing-status"
          className={css({ outline: 'none' })}
        >
          <Text.Header3 bold>{t('headline')}</Text.Header3>
        </div>
        <Text.BodyText3 color="grey70">{t('subtitle')}</Text.BodyText3>

        {/* A real list, so assistive tech announces the item count and position
            rather than five loose rows. */}
        <ul
          aria-label={t('checklist_label')}
          className={`d-flex flex-column align-items-start ${css({
            gap: '16px',
            marginTop: '24px',
            listStyle: 'none',
            padding: 0,
            margin: 0,
          })}`}
        >
          {CHECKLIST_KEYS.map((key, index) => {
            const status = statusForIndex(index, completedCount)
            const isActive = status === 'active'
            return (
              <li
                key={key}
                // WCAG 1.3.1: names the row the spinner is on, since the spinner
                // itself is aria-hidden.
                aria-current={isActive ? 'step' : undefined}
                data-testid={isActive ? 'processing-active-step' : undefined}
                className={`d-flex align-items-center ${css({
                  gap: '12px',
                  opacity: status === 'pending' ? PENDING_OPACITY : 1,
                })}`}
              >
                <i
                  className={`fa ${ICON_BY_STATUS[status]} ${css(
                    buildChecklistIconStyle(status)
                  )}`}
                  aria-hidden="true"
                />
                <Text.BodyText3 bold={isActive}>
                  {t(checklistLabelKey(key, status))}
                </Text.BodyText3>
                {/* Carries done/active/pending, which is otherwise conveyed only
                    by icon shape, colour, weight and opacity. Deliberately NOT
                    in a live region — it is discoverable on demand, not
                    announced per step. */}
                <span className="sr-only">{t(`step_state.${status}`)}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default Processing
